import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import { UniversalModuleView } from '../../../ui-components/listing';
import type { StaticPage } from './model';
import StatusTag from '../../../ui-components/StatusTag';

const formatDate = (iso: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
};

const columns: ColDef<StaticPage>[] = [
  {
    headerName: 'PAGE NAME',
    field: 'pageName',
    sortable: true,
    minWidth: 220,
    cellClass: 'font-medium text-brand-600',
  },
  {
    headerName: 'LAST UPDATED',
    field: 'lastUpdated',
    sortable: true,
    minWidth: 180,
    valueFormatter: (params) => formatDate(params.value as string),
  },
  {
    headerName: 'UPDATED BY',
    field: 'updatedBy',
    sortable: false,
    minWidth: 160,
  },
  {
    headerName: 'STATUS',
    field: 'status',
    sortable: false,
    minWidth: 100,
  },
];

const StaticPages: React.FC = () => {
  const navigate = useNavigate();

  const renderTableRow = useCallback(
    (row: StaticPage): React.ReactNode[] => [
      <button
        key="pageName"
        type="button"
        className="font-medium text-brand-600 truncate text-left hover:underline outline-none focus:ring-1 focus:ring-brand-500 rounded"
        onClick={() => navigate(`/configurations/static-pages/${row.pageId}`)}
      >
        {row.pageName}
      </button>,
      <span
        key="lastUpdated"
        className="text-sm text-neutral-600 dark:text-neutral-400"
      >
        {formatDate(row.lastUpdated)}
      </span>,
      <span
        key="updatedBy"
        className="text-sm text-neutral-600 dark:text-neutral-400"
      >
        {row.updatedBy ?? '—'}
      </span>,
      <span key="status">
        <StatusTag value={row.status} />
      </span>,
    ],
    [navigate]
  );

  return (
    <UniversalModuleView<StaticPage>
      moduleKey="static-pages"
      title="Static Pages"
      apiEndpoint="api/v1/static-page"
      listMethod="GET"
      columnsConfig={columns}
      rowKey={(row) => row.pageId}
      tableRowRenderer={renderTableRow}
      hideFilterButton={true}
      searchPlaceholder="Search by page name..."
      note={
        <>
          <strong>Note:</strong> All predefined pages always exist. Pages cannot
          be deleted. Click any row to edit content.
        </>
      }
      initialPageSize={10}
    />
  );
};

export default StaticPages;
